import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Stack, Button, IconButton } from '@mui/material'
import { PlusCircle, X } from 'react-feather'
import {
  useMutationWrapper,
  usePatchQuotation,
  getUseQuotationDetailsKey,
} from '@/hooks'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  QuotationAgreementsForm,
  QuotationAgreementsFormData,
} from './QuotationAgreementsForm'
import { FormActionButtons } from '@/features/quotationCreate/FormActionButtons'
import { quotationAgreementsSchema } from './validationSchema'
import { theme } from '@/theme'
import { QuotationDetails } from '@/types'

type QuotationAgreementsFormContainerProps = {
  quotationDetails: QuotationDetails
}

export const QuotationAgreementsFormContainer = ({
  quotationDetails,
}: QuotationAgreementsFormContainerProps): JSX.Element => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [isEditing, setIsEditing] = useState(false)

  const { mutateAsync: updateQuotation, isLoading: isMutating } =
    useMutationWrapper(() => usePatchQuotation(quotationDetails.id))

  const defaultValues = useMemo(() => {
    if (quotationDetails.agreements && quotationDetails.agreements.length > 0) {
      const agreements: QuotationAgreementsFormData = {}

      quotationDetails.agreements.forEach((agreement, index) => {
        agreements[`${index}`] = {
          id: `${index}`,
          agreement,
        }
      })

      return agreements
    }

    return {}
  }, [quotationDetails.agreements])

  const methods = useForm<QuotationAgreementsFormData>({
    mode: 'onChange',
    defaultValues: {},
    values: defaultValues,
    resolver: yupResolver(quotationAgreementsSchema),
  })

  const deleteAgreement = (index: string) => {
    // Get current values
    const currentValues = methods.getValues()

    // Create a new object with reassigned indices
    const newValues: QuotationAgreementsFormData = {}

    // Filter out the deleted item and reassign indices
    let newIndex = 0

    Object.entries(currentValues).forEach(([key, value]) => {
      if (key !== index) {
        newValues[newIndex.toString()] = {
          id: newIndex.toString(),
          agreement: value.agreement,
        }
        newIndex++
      }
    })

    // Reset the form with the new values
    methods.reset(newValues)
    setIsEditing(true)
  }

  const handleSubmit = methods.handleSubmit(async (formData) => {
    enqueueSnackbar('Saving...', {
      variant: 'default',
    })

    const agreements = Object.values(formData).map((item) => item.agreement)

    try {
      await updateQuotation({
        agreements,
      })

      const quotationQueryKey = getUseQuotationDetailsKey(quotationDetails.id)

      await queryClient.invalidateQueries(quotationQueryKey)

      closeSnackbar()
      setIsEditing(false)
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, {
        variant: 'error',
      })
    }
  })

  const handleReset = () => {
    methods.reset({
      ...defaultValues,
    })
    setIsEditing(false)
  }

  const handleAddNewAgreement = () => {
    // Since we're reassigning indices in deleteAgreement, we can simply use the length
    const newIndex = Object.keys(methods.getValues()).length.toString()

    methods.setValue(newIndex, {
      id: newIndex,
      agreement: '',
    })
    setIsEditing(true)
  }

  const lines = methods.watch()

  return (
    <>
      <FormProvider {...methods}>
        <Stack spacing={5}>
          {Object.entries(lines).map(([key]) => (
            <Stack direction="row" alignItems="center" key={key}>
              <QuotationAgreementsForm index={key} />
              <IconButton onClick={() => deleteAgreement(key)}>
                <X size="16px" color={theme.palette.green['500']} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        {(methods.formState.isDirty || isEditing) && (
          <FormActionButtons
            cancelClick={handleReset}
            saveClick={handleSubmit}
            isLoading={isMutating}
            disabled={!methods.formState.isDirty && !isEditing}
          />
        )}
      </FormProvider>
      <Button
        variant="text"
        onClick={handleAddNewAgreement}
        startIcon={<PlusCircle size="16px" />}
        sx={{ mt: 6 }}
      >
        {t('quotations:buttons.addAgreement')}
      </Button>
    </>
  )
}
